import Phaser from 'phaser'
import { GAME_WIDTH, GAME_HEIGHT } from '../../config'
import { BaseMiniGameScene, type MiniGameConfig } from './BaseMiniGameScene'

/**
 * Jarring Mini-Game
 * Pour jam into jars by tilting the pot
 */
export class JarringMiniGameScene extends BaseMiniGameScene {
  private pot!: Phaser.GameObjects.Image
  private jar!: Phaser.GameObjects.Image
  private jamFill!: Phaser.GameObjects.Graphics
  private fillLine!: Phaser.GameObjects.Line
  private pourStream!: Phaser.GameObjects.Graphics

  private jarsFilled: number = 0
  private totalJars: number = 5
  private currentFillLevel: number = 0
  private targetFillLevel: number = 0.75 // Fill to 75% of jar
  private isPourng: boolean = false
  private potAngle: number = 0
  private jarComplete: boolean = false

  // Jar dimensions
  private readonly JAR_X = GAME_WIDTH / 2
  private readonly JAR_Y = GAME_HEIGHT / 2 + 100
  private readonly JAR_WIDTH = 80
  private readonly JAR_HEIGHT = 120
  private readonly FILL_TOLERANCE = 0.05 // ±5% for perfect

  constructor() {
    super({ key: 'JarringMiniGameScene' })
  }

  getConfig(): MiniGameConfig {
    return {
      maxScore: this.totalJars * 150, // 150 points per perfect fill
      totalItems: this.totalJars,
    }
  }

  getNextScene(): string | undefined {
    return undefined // Final mini-game, goes to recipe complete
  }

  protected createBackground(): void {
    // Kitchen shelf background
    const graphics = this.add.graphics()

    // Warm wooden shelf colors
    const topColor = Phaser.Display.Color.HexStringToColor('#f5e6d3')
    const bottomColor = Phaser.Display.Color.HexStringToColor('#8b7355')

    for (let y = 0; y < GAME_HEIGHT; y++) {
      const ratio = y / GAME_HEIGHT
      const color = Phaser.Display.Color.Interpolate.ColorWithColor(
        topColor,
        bottomColor,
        100,
        ratio * 100
      )
      graphics.fillStyle(Phaser.Display.Color.GetColor(color.r, color.g, color.b))
      graphics.fillRect(0, y, GAME_WIDTH, 1)
    }

    // Shelf line
    graphics.fillStyle(0x5d4037)
    graphics.fillRect(0, GAME_HEIGHT / 2 + 170, GAME_WIDTH, 8)
  }

  setupGame(): void {
    this.jarsFilled = 0
    this.currentFillLevel = 0
    this.isPourng = false
    this.potAngle = 0
    this.jarComplete = false

    this.createPot()
    this.createJar()
    this.createPourStream()
    this.createInstructions()
    this.setupInput()
  }

  private createPot(): void {
    this.pot = this.add.image(GAME_WIDTH / 2, 150, 'pot_side')
    this.pot.setScale(1.2)
    this.pot.setOrigin(0.3, 0.5) // Pivot point for tilting
  }

  private createJar(): void {
    // Jar container
    this.jar = this.add.image(this.JAR_X, this.JAR_Y, 'jar_empty')
    this.jar.setScale(1.5)

    // Fill indicator (graphics that fill up)
    this.jamFill = this.add.graphics()
    this.jamFill.setDepth(1)

    // Target fill line
    const lineY = this.JAR_Y + (this.JAR_HEIGHT / 2) - (this.JAR_HEIGHT * this.targetFillLevel)
    this.fillLine = this.add.line(
      0, 0,
      this.JAR_X - 50, lineY,
      this.JAR_X + 50, lineY,
      0x4caf50
    )
    this.fillLine.setLineWidth(3)
    this.fillLine.setOrigin(0, 0)

    // Dashed effect
    this.fillLine.setAlpha(0.8)

    // Label
    const fillLabel = this.add.text(this.JAR_X + 55, lineY, '← Fill here', {
      fontFamily: 'system-ui',
      fontSize: '14px',
      color: '#4caf50',
    })
    fillLabel.setOrigin(0, 0.5)

    this.updateJamFill()
  }

  private updateJamFill(): void {
    this.jamFill.clear()

    if (this.currentFillLevel <= 0) return

    // Calculate fill rectangle
    const jarBottom = this.JAR_Y + this.JAR_HEIGHT / 2 - 10
    const fillHeight = this.JAR_HEIGHT * this.currentFillLevel * 0.85
    const fillTop = jarBottom - fillHeight
    const fillWidth = this.JAR_WIDTH - 20

    // Jam color with slight gradient effect
    this.jamFill.fillStyle(0xdc143c, 0.9)
    this.jamFill.fillRoundedRect(
      this.JAR_X - fillWidth / 2,
      fillTop,
      fillWidth,
      fillHeight,
      { tl: 0, tr: 0, bl: 8, br: 8 }
    )

    // Highlight
    this.jamFill.fillStyle(0xff6b6b, 0.4)
    this.jamFill.fillRect(
      this.JAR_X - fillWidth / 2 + 5,
      fillTop + 5,
      10,
      Math.max(0, fillHeight - 10)
    )
  }

  private createPourStream(): void {
    this.pourStream = this.add.graphics()
    this.pourStream.setDepth(5)
  }

  private updatePourStream(): void {
    this.pourStream.clear()

    if (!this.isPourng || this.jarComplete) return

    // Calculate pour start point based on pot tilt
    const pourStartX = this.pot.x + Math.cos(this.potAngle - Math.PI / 4) * 60
    const pourStartY = this.pot.y + Math.sin(this.potAngle - Math.PI / 4) * 30 + 20

    // Pour end point (top of jar)
    const pourEndX = this.JAR_X
    const pourEndY = this.JAR_Y - this.JAR_HEIGHT / 2 + 10

    // Draw wavy pour stream
    this.pourStream.lineStyle(8, 0xdc143c, 0.9)

    const points: Phaser.Math.Vector2[] = []
    const steps = 10
    for (let i = 0; i <= steps; i++) {
      const t = i / steps
      const x = Phaser.Math.Interpolation.Linear([pourStartX, pourEndX], t)
      const baseY = Phaser.Math.Interpolation.Linear([pourStartY, pourEndY], t)
      const wobble = Math.sin(t * Math.PI * 3 + this.time.now * 0.01) * 3 * (1 - t)
      points.push(new Phaser.Math.Vector2(x + wobble, baseY))
    }

    this.pourStream.beginPath()
    this.pourStream.moveTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) {
      this.pourStream.lineTo(points[i].x, points[i].y)
    }
    this.pourStream.strokePath()

    // Drip particles at end
    if (Math.random() > 0.7) {
      this.createDripParticle(pourEndX, pourEndY)
    }
  }

  private createDripParticle(x: number, y: number): void {
    const drip = this.add.circle(
      x + Phaser.Math.Between(-5, 5),
      y,
      Phaser.Math.Between(2, 4),
      0xdc143c,
      0.8
    )

    this.tweens.add({
      targets: drip,
      y: y + 20,
      alpha: 0,
      scale: 0.5,
      duration: 300,
      onComplete: () => drip.destroy(),
    })
  }

  private createInstructions(): void {
    const text = this.add.text(
      GAME_WIDTH / 2,
      70,
      '↕ Drag up/down to tilt the pot and pour!',
      {
        fontFamily: 'system-ui',
        fontSize: '18px',
        color: '#5d4037',
      }
    )
    text.setOrigin(0.5)
    text.setAlpha(0.8)
  }

  private setupInput(): void {
    this.input.on('pointerdown', () => {
      if (this.isComplete || this.jarComplete) return
      this.isPourng = true
    })

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isComplete || this.jarComplete) return

      // Map Y position to pot angle
      // Higher Y (lower on screen) = more tilt = faster pour
      const normalizedY = (pointer.y - 100) / (GAME_HEIGHT - 200)
      const clampedY = Phaser.Math.Clamp(normalizedY, 0, 1)

      // Convert to angle (0 = upright, PI/3 = tilted)
      this.potAngle = clampedY * (Math.PI / 3)
      this.pot.setRotation(this.potAngle)
    })

    this.input.on('pointerup', () => {
      this.isPourng = false
      this.potAngle = 0

      // Animate pot back to upright
      this.tweens.add({
        targets: this.pot,
        rotation: 0,
        duration: 200,
        ease: 'Power2',
      })

      // Check if jar is filled
      if (this.currentFillLevel > 0.3 && !this.jarComplete) {
        this.completeJar()
      }
    })
  }

  update(): void {
    if (this.isComplete || this.jarComplete) return

    // Pour jam based on pot angle
    if (this.isPourng && this.potAngle > 0.1) {
      const pourRate = (this.potAngle / (Math.PI / 3)) * 0.015
      this.currentFillLevel = Math.min(1.2, this.currentFillLevel + pourRate) // Allow overfill

      this.updateJamFill()
      this.updatePourStream()

      // Check for major overfill (spill)
      if (this.currentFillLevel >= 1.1) {
        this.createSpillEffect()
      }
    } else {
      this.pourStream.clear()
    }
  }

  private createSpillEffect(): void {
    // Jam dripping down the side
    const side = Math.random() > 0.5 ? 1 : -1
    const x = this.JAR_X + side * (this.JAR_WIDTH / 2 - 5)
    const y = this.JAR_Y - this.JAR_HEIGHT / 2

    const drip = this.add.circle(x, y, 4, 0xdc143c, 0.9)

    this.tweens.add({
      targets: drip,
      y: this.JAR_Y + this.JAR_HEIGHT / 2 + 20,
      duration: 800,
      ease: 'Power2',
      onComplete: () => drip.destroy(),
    })
  }

  private completeJar(): void {
    this.jarComplete = true
    this.isPourng = false
    this.pourStream.clear()

    // Calculate score based on fill accuracy
    const fillDiff = Math.abs(this.currentFillLevel - this.targetFillLevel)
    let points: number
    let feedbackText: string
    let feedbackColor: number

    if (fillDiff <= this.FILL_TOLERANCE) {
      points = 150
      feedbackText = 'Perfect!'
      feedbackColor = 0xffd700
      this.createSparkles()
    } else if (fillDiff <= this.FILL_TOLERANCE * 2) {
      points = 100
      feedbackText = 'Great!'
      feedbackColor = 0x4caf50
    } else if (this.currentFillLevel > this.targetFillLevel) {
      if (this.currentFillLevel >= 1.0) {
        points = 25
        feedbackText = 'Spilled!'
        feedbackColor = 0xf44336
      } else {
        points = 50
        feedbackText = 'Overfilled'
        feedbackColor = 0xff9800
      }
    } else {
      points = 75
      feedbackText = 'Underfilled'
      feedbackColor = 0x2196f3
    }

    this.addScore(points, this.JAR_X, this.JAR_Y - 80)
    this.showFeedback(feedbackText, this.JAR_X, this.JAR_Y - 110, feedbackColor)

    // Add lid animation
    this.addJarLid()

    // Move to next jar
    this.jarsFilled++
    this.incrementProgress()

    if (!this.isComplete) {
      this.time.delayedCall(1200, () => this.nextJar())
    }
  }

  private addJarLid(): void {
    const lid = this.add.image(this.JAR_X, this.JAR_Y - this.JAR_HEIGHT / 2 - 30, 'jar_lid')
    lid.setScale(1.5)

    // Lid drops down animation
    this.tweens.add({
      targets: lid,
      y: this.JAR_Y - this.JAR_HEIGHT / 2 - 5,
      duration: 300,
      ease: 'Bounce.easeOut',
    })
  }

  private createSparkles(): void {
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2
      const dist = 60
      const x = this.JAR_X + Math.cos(angle) * dist
      const y = this.JAR_Y + Math.sin(angle) * dist

      const sparkle = this.add.star(x, y, 4, 4, 8, 0xffd700)

      this.tweens.add({
        targets: sparkle,
        scale: 0,
        alpha: 0,
        rotation: Math.PI,
        duration: 600,
        delay: i * 50,
        onComplete: () => sparkle.destroy(),
      })
    }
  }

  private nextJar(): void {
    // Slide current jar off screen
    this.tweens.add({
      targets: [this.jar, this.jamFill],
      x: '-=200',
      alpha: 0,
      duration: 400,
      ease: 'Power2',
      onComplete: () => {
        // Reset for new jar
        this.currentFillLevel = 0
        this.jarComplete = false

        // Reposition jar
        this.jar.x = this.JAR_X + 200
        this.jar.setAlpha(1)

        // Slide in new jar
        this.tweens.add({
          targets: this.jar,
          x: this.JAR_X,
          duration: 400,
          ease: 'Power2',
        })

        this.updateJamFill()
      },
    })
  }
}
